import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { pushLayer } from "../utils/layers";
import { onSwipe } from "../utils/gestures";
import { useIsMobile } from "../utils/viewport";

/**
 * Окно (мобильный аудит, M-12). На телефоне (< md) — на весь экран по реальной видимой
 * высоте (--app-vvh: клавиатура не перекрывает поле ввода), одна прокрутка, липкий подвал,
 * крестик 44×44; фон заблокирован, «Назад» телефона закрывает окно. На компьютере:
 * desktop="center" — прежняя карточка по центру; desktop="none" — компонент рисует
 * компьютерный вид сам, Sheet на ≥ md ничего не выводит (случай плавающего чата).
 */
export function useLayer(open: boolean, onClose: () => void, history = true, inertRoot = true) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    // Окно в портале — остальное приложение недоступно для фокуса; окно внутри #root — нет.
    const root = inertRoot ? document.getElementById("root") : null;
    if (root) root.inert = true;
    const pop = pushLayer(() => close.current(), { history });
    return () => {
      pop();
      if (root && !document.documentElement.dataset.sheet) root.inert = false;
      opener?.focus?.({ preventScroll: true });
    };
  }, [open, history, inertRoot]);
}

/**
 * Для окон, которые рисуются внутри страницы, а не через Sheet: фон, «Назад», Esc (P2-01).
 * Ставится перед карточкой окна: на телефоне свайп вниз по карточке закрывает нижний лист
 * (P3-02); swipe={false} — если в окне есть черновик.
 */
export function ModalLayer({ onClose, swipe = true }: { onClose: () => void; swipe?: boolean }) {
  useLayer(true, onClose, true, false);
  const marker = useRef<HTMLElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const card = marker.current?.nextElementSibling;
    if (!swipe || !(card instanceof HTMLElement)) return;
    return onSwipe(card, "down", () => { if (window.matchMedia("(max-width: 639px)").matches) close.current(); });
  }, [swipe]);
  return <i ref={marker} hidden />;
}

type Props = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Своя шапка вместо заголовка с крестиком (чат); крестик тогда рисует сам компонент. */
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  size?: "md" | "lg" | "2xl";
  desktop?: "center" | "none";
  bodyClassName?: string;
};

const SIZE = { md: "max-w-md", lg: "max-w-lg", "2xl": "max-w-2xl" };

export default function Sheet({ open, onClose, title, header, footer, children, size = "2xl", desktop = "center", bodyClassName = "" }: Props) {
  const mobile = useIsMobile();
  const titleId = useId();
  const shown = open && (mobile || desktop === "center");
  useLayer(shown, onClose);
  if (!shown) return null;

  const head = header ?? (
    <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2 sm:px-5 sm:py-3">
      <h3 id={titleId} className="min-w-0 truncate font-display text-base font-semibold">{title}</h3>
      <button onClick={onClose} aria-label="Закрыть"
        className="-mr-2 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 cursor-pointer">
        <X className="h-5 w-5" />
      </button>
    </div>
  );

  if (mobile) {
    return createPortal(
      <div role="dialog" aria-modal="true" aria-labelledby={header ? undefined : titleId}
        className="fixed inset-x-0 z-(--z-sheet) flex flex-col bg-white"
        style={{ top: "var(--app-vv-top, 0px)", height: "var(--app-vvh, 100dvh)" }}>
        <div className="shrink-0 pt-[env(safe-area-inset-top)]">{head}</div>
        <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${bodyClassName}`}>{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-slate-100 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">{footer}</div>
        )}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-(--z-sheet) flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby={header ? undefined : titleId}
        className={`my-8 w-full ${SIZE[size]} rounded-2xl bg-white shadow-xl`}>
        {head}
        <div className={`max-h-[70vh] overflow-y-auto ${bodyClassName}`}>{children}</div>
        {footer && <div className="border-t border-slate-100 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
